import { useState, useRef, useLayoutEffect } from "react";
import { cn } from "@githits/shared";
import { sessions } from "../data";
import { useReplay } from "../hooks/use-replay";
import QuestionSelector from "./QuestionSelector";
import MockSearchBar from "./MockSearchBar";
import QueryThread from "./QueryThread";
import ReferencesPanel from "./ReferencesPanel";
import CodeExamplePanel from "./CodeExamplePanel";

const EXPAND_DURATION_MS = 500;

export default function ReplayPage() {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [overlayExiting, setOverlayExiting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lockedHeightRef = useRef<number | null>(null);
  const pendingKeyRef = useRef<string | null>(null);
  const replay = useReplay();

  const handleSelect = (key: string) => {
    const session = sessions[key];
    if (!session) return;

    setActiveKey(key);
    setSelectorOpen(false);

    if (!hasStarted) {
      // Measure compact height BEFORE React re-renders with expanded classes
      if (containerRef.current) {
        lockedHeightRef.current = containerRef.current.offsetHeight;
      }
      pendingKeyRef.current = key;
      setOverlayExiting(true);
      setHasStarted(true);
    } else {
      replay.start(session);
    }
  };

  // After React commits the expanded DOM, animate from locked height to natural height
  useLayoutEffect(() => {
    if (!overlayExiting || !containerRef.current || lockedHeightRef.current === null) return;

    const container = containerRef.current;
    const startHeight = lockedHeightRef.current;

    // Measure the expanded height (DOM already has expanded classes, before paint)
    const targetHeight = container.offsetHeight;

    // Lock to compact height — prevents the flash of expanded content.
    // Must also override min-height (CSS class sets min-h-[60vh] which would
    // win over a smaller explicit height).
    container.style.height = `${startHeight}px`;
    container.style.minHeight = "0px";
    container.style.overflow = "hidden";

    // Force reflow so the browser commits the locked height
    void container.offsetHeight;

    // Animate from compact to expanded (both explicit px values — CSS can interpolate)
    container.style.transition = `height ${EXPAND_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
    container.style.height = `${targetHeight}px`;

    // Scroll the expanded widget into view, centering it vertically when it
    // fits, otherwise aligning the top to the viewport top.
    const elementTop = window.scrollY + container.getBoundingClientRect().top;
    const offset = Math.max(0, (window.innerHeight - targetHeight) / 2);
    window.scrollTo({ top: Math.max(0, elementTop - offset), behavior: "smooth" });

    const timer = setTimeout(() => {
      // Clean up inline styles — CSS utility classes take over
      container.style.transition = "";
      container.style.height = "";
      container.style.minHeight = "";
      container.style.overflow = "";
      lockedHeightRef.current = null;
      setOverlayExiting(false);

      // Start the replay now that the container is at full size
      const key = pendingKeyRef.current;
      if (key) {
        const session = sessions[key];
        if (session) replay.start(session);
        pendingKeyRef.current = null;
      }
    }, EXPAND_DURATION_MS + 20);

    return () => clearTimeout(timer);
  }, [overlayExiting]); // eslint-disable-line react-hooks/exhaustive-deps

  const isIdle = replay.phase === "idle";
  const isDone = replay.phase === "done";

  // Compact: initial idle state before any question is clicked
  const isCompact = isIdle && !hasStarted && !overlayExiting;

  // Search bar: visible during typing, submitting, AND during expand (so the
  // container's target height includes it — prevents a second jump)
  const showSearchBar =
    replay.phase === "typing" ||
    replay.phase === "submitting" ||
    overlayExiting;

  // Two-panel layout once submitting animation finishes
  const showPanels = !showSearchBar && replay.phase !== "idle";

  // Right panel content visibility
  const showSources = replay.visibleReferences.length > 0;
  const showCode = Boolean(replay.displayedCode);

  // Initial overlay: compact flow or exiting animation
  const showInitialOverlay = isCompact || overlayExiting;
  // "Try another" overlay: absolute with backdrop blur
  const showTryAnotherOverlay = selectorOpen;

  return (
    <div
      ref={containerRef}
      className={cn(
        "ghw-relative ghw-max-w-7xl ghw-mx-auto ghw-px-4 sm:ghw-px-6",
        isCompact ? "ghw-py-4" : "ghw-py-8 ghw-min-h-[60vh]"
      )}
    >
      {/* Main replay content */}
      <div>
        {/* Search bar — vertically centered in the panel area */}
        {showSearchBar && (
          <div className="ghw-flex ghw-items-center ghw-justify-center ghw-h-[70vh] ghw-max-h-[700px]">
            <div className="ghw-w-full ghw-max-w-3xl">
              <MockSearchBar
                displayedQuery={replay.displayedQuery}
                cursorVisible={replay.cursorVisible}
                language={replay.language}
                phase={replay.phase}
              />
            </div>
          </div>
        )}

        {/* Two-panel layout — fades in after search bar exits */}
        {showPanels && (
          <div className="ghw-grid ghw-grid-cols-1 lg:ghw-grid-cols-2 ghw-gap-4 lg:ghw-gap-0 animate-replay-panels-enter">
            {/* LEFT: Query Thread */}
            <div className="lg:ghw-border-r lg:ghw-border-border ghw-h-[70vh] ghw-max-h-[700px] ghw-rounded-lg ghw-border ghw-border-border lg:ghw-rounded-r-none ghw-bg-secondary/30">
              <QueryThread
                query={replay.displayedQuery}
                phase={replay.phase}
                visibleSearchQueries={replay.visibleSearchQueries}
                exampleTitle={replay.exampleTitle}
                explanation={replay.explanation}
              />
            </div>

            {/* RIGHT: Sources and Example */}
            <div className="ghw-flex ghw-flex-col ghw-h-[70vh] ghw-max-h-[700px] ghw-rounded-lg ghw-border ghw-border-border lg:ghw-rounded-l-none lg:ghw-border-l-0 ghw-bg-secondary/30">
              <div className="ghw-px-4 ghw-py-3 ghw-border-b ghw-border-border ghw-shrink-0">
                <h3 className="ghw-text-sm ghw-font-medium ghw-text-muted-foreground">
                  Sources and Example
                </h3>
              </div>

              <div className="ghw-flex ghw-flex-col ghw-flex-1 ghw-min-h-0 ghw-p-4 ghw-gap-4">
                {/* References — compact, no scroll, fixed at top */}
                {showSources && (
                  <div className="ghw-shrink-0">
                    <ReferencesPanel references={replay.visibleReferences} />
                  </div>
                )}

                {/* Code — fills remaining space, scrolls internally */}
                {showCode && (
                  <div className="ghw-flex-1 ghw-min-h-0">
                    <CodeExamplePanel
                      code={replay.displayedCode}
                      language={replay.codeLanguage}
                      title={replay.exampleTitle}
                      isStreaming={replay.phase === "generating"}
                    />
                  </div>
                )}

                {!showSources && !showCode && (
                  <p className="ghw-text-sm ghw-text-muted-foreground ghw-italic">
                    Waiting for results...
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* "Try another" button when replay is done */}
        {isDone && !selectorOpen && (
          <div className="ghw-mt-6 ghw-text-center">
            <button
              type="button"
              onClick={() => setSelectorOpen(true)}
              className="ghw-px-4 ghw-py-2.5 ghw-rounded-lg ghw-text-sm ghw-font-medium ghw-bg-secondary ghw-text-primary ghw-border-none ghw-cursor-pointer hover:ghw-bg-secondary/80 ghw-transition-colors"
            >
              Try another question
            </button>
          </div>
        )}
      </div>

      {/* Initial question selector — compact flow layout, fades out on first click */}
      {showInitialOverlay && (
        <div
          className={cn(
            "ghw-flex ghw-items-center ghw-justify-center",
            overlayExiting
              ? "ghw-absolute ghw-inset-0 ghw-z-10 ghw-backdrop-blur-md ghw-bg-background/70 ghw-rounded-lg animate-replay-overlay-exit"
              : "ghw-py-2"
          )}
        >
          <div className="ghw-max-w-5xl ghw-w-full ghw-px-4">
            <QuestionSelector
              sessions={sessions}
              activeKey={activeKey}
              onSelect={handleSelect}
            />
          </div>
        </div>
      )}

      {/* "Try another" overlay — absolute with backdrop blur */}
      {showTryAnotherOverlay && (
        <div className="ghw-absolute ghw-inset-0 ghw-z-10 ghw-flex ghw-items-center ghw-justify-center ghw-backdrop-blur-md ghw-bg-background/70 ghw-rounded-lg">
          <div className="ghw-max-w-5xl ghw-w-full ghw-px-4">
            <h2 className="ghw-text-center ghw-text-2xl ghw-font-lexend ghw-font-semibold ghw-text-primary ghw-mb-6">
              Try another question
            </h2>
            <QuestionSelector
              sessions={sessions}
              activeKey={activeKey}
              onSelect={handleSelect}
            />
          </div>
        </div>
      )}
    </div>
  );
}
